/**
 * #2133 設計 §5 手順1: 今の Flutter Web（flutter_service_worker.js）が入れている
 * Service Worker を解除し、古いキャッシュを消す。React 版を同じ URL に配信した
 * ときに、ブラウザに残った Flutter Web の Service Worker が古い index.html / JS を
 * キャッシュから返し続けて切り替えが反映されない事態を防ぐ。
 *
 * ガード: 一度解除済みであれば次回起動時はスキップする（毎回
 * getRegistrations/caches.keys を呼ぶコストを避ける。解除自体は一度行えば
 * ブラウザ側に Service Worker が残らないため、繰り返す必要がない）。
 */
import { logger } from './logger';

const CLEANUP_DONE_KEY = 'yoctohanuman-web:sw-cleanup-done';

function hasRunCleanup(): boolean {
  try {
    return window.localStorage.getItem(CLEANUP_DONE_KEY) === 'true';
  } catch {
    // localStorage が使えない環境（プライベートモード等）では毎回実行する
    return false;
  }
}

function markCleanupDone(): void {
  try {
    window.localStorage.setItem(CLEANUP_DONE_KEY, 'true');
  } catch {
    // 保存できなくても処理自体は完了しているので無視する
  }
}

export async function cleanupLegacyServiceWorkers(): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  if (hasRunCleanup()) {
    return;
  }

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }

    if ('caches' in window) {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map((key) => caches.delete(key)));
    }

    markCleanupDone();
  } catch (error) {
    logger.error('Failed to clean up legacy service workers', error);
  }
}
